/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useHeroSection } from "@/hooks/useHeroSection";
import Navbar from "@/shared/Navbar";
import Image from "next/image";
import Link from "next/link";
import { Calculator, Users, Star } from "lucide-react";
import { motion } from "framer-motion";
import { usePages } from "@/hooks/usePages";
import { useMemo } from "react";

const AboutUs = () => {
  const { data: pages, loading } = usePages();
  const features = [
    {
      icon: <Calculator className="w-10 h-15 text-white" />,
      title: (
        <>
          Saves <span className="text-[#FBB03B] font-semibold">1000</span>{" "}
          Dollars Per Year
        </>
      ),
      description:
        "We’re committed to saving you the most amount of money by finding you the most competitive rates on the market.",
    },
    {
      icon: <Users className="w-10 h-15 text-white" />,
      title: (
        <>
          Serves <span className="text-[#FBB03B] font-semibold">500+</span>{" "}
          Nationwide
        </>
      ),
      description:
        "We’re helping hundreds of people buy and sell the homes of their dreams across the country.",
    },
    {
      icon: <Star className="w-10 h-15 text-white" />,
      title: (
        <>
          Ranks <span className="text-[#FBB03B] font-semibold">4.5</span> Stars
          On TrustPilot
        </>
      ),
      description:
        "Our users trust us to make better decisions about real estate, backed by transparent reviews and proven success.",
    },
  ];

  const HeroSection = useHeroSection({
    title: "Connecting People. Building Trust.",
    subtitle:
      "At AskLocal, we believe buying or selling a home should feel empowering  not overwhelming. We connect you with trusted local experts and bring every step of the journey together in one smart platform.",
    buttonText: "Apply For Agent",
    backgroundImage: "/about-us/aboutUs-hero.png",
    buttonLink: "/asklocal-agent",
  });
  const howToHubDropdown = useMemo(() => {
    const staticPages = [
      {
        label: "Property Intelligence Report",
        href: "/how-to-hub/property-intelligence-report",
      },
      { label: "Mortgage Guide", href: "/how-to-hub/mortgage-guide" },
    ];

    const dynamicPages =
      pages?.map((p: any) => ({
        label: p.title || "Untitled",
        href: `/how-to-hub/${p.slug}`,
      })) || [];

    return [
      ...(dynamicPages.length
        ? dynamicPages
        : loading
        ? [{ label: "Loading...", href: "#" }]
        : []),
      ...staticPages,
    ];
  }, [pages, loading]);
  return (
    <div className="bg-[#FFFFFF]">
      {/* 🔹 Navbar */}
      <Navbar
        defaultLogo="/logo/asklocal-white-logo.png"
        scrolledLogo="/logo/color-full-logo.png"
        isLight
        labelColor="#ffffff"
        labelHoverColor="#EB9C2C"
        buttonBg="#EB9C2C"
        buttonTextColor="#FFFFFF"
        buttonHoverBg="#4177BC"
        buttonHoverText="#ffffff"
        ctaText="Looking to Buy/Sell?"
        ctaLink="/buy-sell"
        navItems={[
          { label: "Home", href: "/" },
          {
            label: "Property Reports",
            href: "/how-to-hub/property-intelligence-report",
          },
          {
            label: "How-To-Hub",
            dropdown: howToHubDropdown, // ✅ dynamic + static pages
          },
          {
            label: "About Us",
            href: "/about-us",
          },
          { label: "Pro-finder", href: "/pro-finder" },
        ]}
      />

      {/* 🔹 Hero Section */}
      {HeroSection}

      {/* 🔹 The Vision Behind AskLocal */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
        className="py-10 px-4 sm:px-8 md:px-16 lg:px-24 container mx-auto text-center md:text-left"
      >
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-5xl sm:text-4xl md:text-[78px] judson-regular text-[#000000] mb-6"
        >
          The Vision Behind <br /> AskLocal
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="text-[#938E8E] inter-regular text-base sm:text-lg leading-relaxed max-w-[900px] mb-12"
        >
          AskLocal was founded to make real estate transparent, accessible, and
          stress-free. We saw too many people struggling to juggle multiple
          apps, endless phone calls, and confusing processes so we built one
          secure platform that connects clients, agents, lenders, and inspectors
          all in one place.
        </motion.p>

        <motion.div
          whileHover={{ scale: 1.03 }}
          transition={{ type: "spring", stiffness: 150, damping: 14 }}
          className="w-full rounded-2xl overflow-hidden shadow-lg"
        >
          <Image
            src="/about-us/vision-hero.png"
            alt="AskLocal Vision"
            width={1300}
            height={650}
            className="object-cover w-full lg:h-auto h-70"
          />
        </motion.div>
      </motion.section>

      {/* 🔹 Buying or Selling Section */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
        className="lg:py-10 bg-[#FFFFFF] px-4 sm:px-8 md:px-16 lg:px-24 max-w-[1300px] mx-auto"
      >
        <h2 className="text-3xl sm:text-4xl md:text-[78px] text-center font-semibold text-[#111827] judson-regular mb-4">
          Buying Or Selling A Home <br className="hidden sm:block" /> Shouldn’t
          Be This Hard
        </h2>
        <p className="text-[#6B7280] text-base text-center sm:text-lg mb-16 max-w-[800px] mx-auto">
          Despite technological advances and the efforts of many great
          professionals, the process remains fragmented and unclear. That’s why
          we’ve reimagined the experience with <br /> solutions built around
          your needs.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
          {[1, 2].map((_, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.04, y: -8 }}
              transition={{ type: "spring", stiffness: 220, damping: 16 }}
              className="bg-[#FAFAFA] rounded-2xl p-8 flex flex-col shadow-sm hover:shadow-lg transition-all duration-300 h-full"
            >
              <Image
                src={
                  index === 0 ? "/about-us/frane.png" : "/about-us/Frame.png"
                }
                alt={index === 0 ? "Buyers and Sellers" : "Real Estate Agents"}
                width={360}
                height={400}
                className="mx-auto mb-6 object-contain"
              />
              <h3 className="text-[48px] judson-regular mb-3 text-[#000000]">
                {index === 0 ? "Buyers And Sellers" : "Real Estate Agents"}
              </h3>
              <p className="text-[#716B6B] inter-regular mb-8 leading-relaxed">
                {index === 0
                  ? "Find local experts, compare agents, and secure financing for your next home. AskLocal makes buying or selling simple, stress-free, and smart."
                  : "Tap into a network of motivated clients who value your service. Manage deals, build trust, and grow your reach — all in one place."}
              </p>
              <div className="mt-auto flex justify-center">
                <Link href="blogs">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: "spring", stiffness: 250 }}
                    className="bg-[#4177BC] hover:bg-[#EB9C2C] text-[#FFFFFF] inter-semibold w-90 py-3 rounded-lg transition-all duration-300"
                  >
                    Learn More
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* 🔹 Why Homeowners Choose AskLocal */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
        className="py-24 bg-white px-4 sm:px-8 md:px-16 lg:px-24 text-center"
      >
        <h2 className="text-3xl sm:text-4xl md:text-[78px] judson-regular font-bold text-[#000000] mb-5">
          Why Homeowners <br /> Choose AskLocal
        </h2>
        <p className="text-[#938E8E] inter-regular text-base sm:text-lg mb-20 max-w-[900px] mx-auto leading-relaxed">
          Buying or selling a home is one of the biggest financial moves of your
          life. AskLocal was built to eliminate the stress, confusion and
          guesswork by combining trusted local professionals <br /> with
          technology that puts you in control.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 max-w-[1100px] mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2, duration: 0.6 }}
              whileHover={{ scale: 1.05, y: -6 }}
              className="bg-[#FFFFFF] rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 text-left flex flex-col"
            >
              <motion.div
                whileHover={{ rotate: 12 }}
                transition={{ type: "spring", stiffness: 200 }}
                className="bg-[#EB9C2C] w-20 h-20 rounded-lg flex items-center justify-center mb-6"
              >
                {feature.icon}
              </motion.div>
              <h3 className="text-[#000000] judson-bold text-2xl font-semibold mb-2">
                {feature.title}
              </h3>
              <p className="text-[#716B6B] inter-regular text-base leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.section>
    </div>
  );
};

export default AboutUs;
